/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Removed local error prop, relying on global error handling.
 *                                   - Integrated Spinner for submission state.
 *                                   - Ensured `cost` is parsed as float.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide fields
 *                                   and customize field titles based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component handles user input for fixed value definitions.
 *                      It does not process sensitive data directly or interact with external systems.
 * INTENT: Provide a reusable form for creating and editing FixedValue records,
 *         ensuring data integrity and a clear user experience, now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure input validation is robust and
 *                        that the form state correctly reflects submission status.
 * LLM_CONFIDENCE: 100% - Standard form component with state management and input handling.
 */
import React, { useState, useEffect } from 'react';
import { FixedValue, FixedValueCreate } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Checkbox } from './ui/checkbox.tsx';
import { Spinner } from './ui/spinner.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface FixedValueFormProps {
  initialData?: FixedValue | null;
  onSubmit: (data: FixedValueCreate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  entityId: string; // NEW: Add entityId prop
}

const FixedValueForm: React.FC<FixedValueFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const [formData, setFormData] = useState<FixedValueCreate>({
    description: '',
    cost: 0,
    active: 1,
    created_by: 'admin_user',
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        description: initialData.description,
        cost: initialData.cost,
        active: initialData.active ?? 1,
        created_by: initialData.created_by || 'admin_user',
      });
    } else {
      setFormData({ description: '', cost: 0, active: 1, created_by: 'admin_user' });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === 'checkbox' ? (checked ? 1 : 0) : parseFloat(value) || 0,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isFieldVisible('description') && (
        <div>
          <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('description', 'Description')}
          </Label>
          <Input
            id="description"
            type="text"
            required
            value={formData.description}
            onChange={handleChange}
          />
        </div>
      )}
      {isFieldVisible('cost') && (
        <div>
          <Label htmlFor="cost" className="block text-sm font-medium text-foreground mb-1">
            {getFieldTitle('cost', 'Cost')}
          </Label>
          <Input
            id="cost"
            type="number"
            step="0.01"
            required
            value={formData.cost}
            onChange={handleChange}
          />
        </div>
      )}
      {isFieldVisible('active') && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id="active"
            checked={formData.active === 1}
            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, active: checked ? 1 : 0 }))}
          />
          <Label htmlFor="active" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
            {getFieldTitle('active', 'Active')}
          </Label>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default FixedValueForm;